import { selectSuggestedDocumentType, SuggestedDocumentType } from './document-analysis.model';
import { inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkFileResponse } from '../common/service/document.service';
import { DocumentMetadata } from './document-metadata.model';
import { DocumentService } from '../common';
import { NotificationService } from '../common/service/notification.service';
import { DocumentManagerImportService } from './document-manager-import.service';
import { ManagedDocument } from '../common/models/document.model';
import { DocumentPageCacheService } from './document-page-cache.service';
import { Document } from '../model';
import { formatJsonDate } from '../util';
import { hashBlob, hashDataUrl } from './document-hash.util';

@Injectable({ providedIn: 'root' })
export class DocumentUploadService {
  private readonly documentService = inject(DocumentService);
  private readonly importer = inject(DocumentManagerImportService);
  private readonly pageCache = inject(DocumentPageCacheService);
  private readonly notification = inject(NotificationService);

  readonly uploadingDocuments = signal<Set<string>>(new Set());

  setUploadingState(documentId: string, uploading: boolean): void {
    this.uploadingDocuments.update((current) => {
      const next = new Set(current);
      if (uploading) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  }

  async uploadDocument(
    document: ManagedDocument,
    options: {
      ensureDocumentType: (doc: ManagedDocument) => Promise<ManagedDocument | undefined>;
      onUploaded: (uploaded: Document) => Promise<void>;
    }
  ): Promise<void> {
    const readyDocument = await options.ensureDocumentType(document);
    if (!readyDocument?.documentTypeId || !readyDocument.documentDate) {
      return;
    }
    this.setUploadingState(document.id, true);
    try {
      const uploaded = await this.uploadReviewedDocument(readyDocument, {
        title: readyDocument.title,
        documentTypeId: readyDocument.documentTypeId,
        documentDate: new Date(readyDocument.documentDate),
      });
      if (!uploaded) {
        return;
      }
      const formattedDate = formatJsonDate(readyDocument.documentDate);
      if (formattedDate) {
        try {
          await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
          uploaded.date = formattedDate;
        } catch (error) {
          console.error('Failed to update document date', error);
          throw error;
        }
      }
      await this.notification.success('Document_uploaded_to_server_tr');
      await options.onUploaded(uploaded);
    } catch (error) {
      console.error('Failed to upload document', error);
      throw error;
    } finally {
      this.setUploadingState(document.id, false);
    }
  }

  async ensureWorkFile(document: ManagedDocument, pending?: Promise<WorkFileResponse>): Promise<string> {
    if (document.workFileHash) return document.workFileHash;
    if (pending) {
      try {
        const uploaded = await pending;
        document.workFileHash = uploaded.fileHash;
        document.pages ??= uploaded.pageCount;
        return uploaded.fileHash;
      } catch {
        // Retry the initial upload if it failed while the user reviewed the local preview.
      }
    }
    const blob = await this.importer.documentToBlob(document);
    const file = new File([blob], `${document.title}.${document.extension}`, { type: document.mimeType });
    const uploaded = await this.documentService.uploadWorkFile(file);
    document.workFileHash = uploaded.fileHash;
    document.pages ??= uploaded.pageCount;
    return uploaded.fileHash;
  }

  async uploadReviewedDocument(document: ManagedDocument, metadata: DocumentMetadata, selectedPages?: number[]) {
    if (!metadata.documentTypeId) throw new Error('Document type is required');
    await this.ensureWorkFile(document);
    const pages =
      selectedPages === undefined
        ? Array.from({ length: document.kind === 'image' ? 1 : (document.pages ?? 0) }, (_, index) => index + 1)
        : [...new Set(selectedPages)].sort((a, b) => a - b);
    if (
      !pages.length ||
      (document.kind === 'image' && (pages.length !== 1 || pages[0] !== 1)) ||
      pages.some(
        (page) => !Number.isInteger(page) || page < 1 || (document.pages !== undefined && page > document.pages)
      )
    )
      throw new Error('Invalid document pages');
    const title = metadata.title.trim() || document.title;
    const fileName = `${title}.${document.extension}`;
    const submit = () =>
      this.documentService.uploadFromWorkFileWithDuplicateConfirm(
        document.workFileHash!,
        pages,
        fileName,
        metadata.documentTypeId!,
        title,
        ...(metadata.duplicateCheckCompleted
          ? [{ allowDuplicate: metadata.allowDuplicate === true, skipDuplicateDialog: true }]
          : [])
      );
    try {
      return await submit();
    } catch (error) {
      const errorType =
        error instanceof HttpErrorResponse ? (error.error?.error_type ?? error.error?.errorType) : undefined;
      if (
        errorType !== 'work_file_not_found' ||
        !(error instanceof HttpErrorResponse) ||
        (error.status !== 404 && error.status !== 410)
      )
        throw error;
      document.workFileHash = undefined;
      await this.ensureWorkFile(document);
      return await submit();
    }
  }

  async buildUploadHashes(document: ManagedDocument): Promise<{ fileHash: string; pageHashes?: (string | null)[] }> {
    // fileHash = SHA-256 of raw file bytes (matches backend verification + docs-service cache key)
    const fileHash = await hashBlob(await this.importer.documentToBlob(document));

    // Page cache keys use the dataUrl hash (frontend-only IndexedDB cache)
    let pageHashes: (string | null)[] | undefined;
    if (document.kind === 'pdf') {
      const totalPages = document.pages ?? 0;
      if (totalPages > 0) {
        const pageCacheKey = await this.computeDocumentHash(document);
        const cached = await this.pageCache.listDocumentPageHashes(pageCacheKey);
        pageHashes = Array.from({ length: totalPages }, (_, index) => cached.get(index + 1) ?? null);
      }
    }
    return { fileHash, pageHashes };
  }

  async suggestDocumentType(document: ManagedDocument): Promise<SuggestedDocumentType | undefined> {
    if (document.documentTypeId) {
      return {
        documentTypeId: document.documentTypeId,
        documentTypeName: document.documentTypeName ?? undefined,
        documentTypeConfidence: undefined,
      };
    }
    try {
      const blob = await this.importer.documentToBlob(document);
      const filename = `${document.title}.${document.extension}`;
      const file = new File([blob], filename, { type: document.mimeType });
      const workFile = document.workFileHash
        ? { fileHash: document.workFileHash, pageCount: document.pages }
        : await this.documentService.uploadWorkFile(file);
      document.workFileHash = workFile.fileHash;
      const pageCount = workFile.pageCount ?? document.pages ?? 1;
      const pages = Array.from({ length: Math.max(pageCount, 1) }, (_, index) => index + 1);
      const response = await this.documentService.analyzeWorkFileWhenReady(workFile.fileHash, pages);
      return selectSuggestedDocumentType(response?.analysis, pages);
    } catch (error) {
      console.warn('Failed to analyze document type suggestion', error);
      return undefined;
    }
  }

  async computeDocumentHash(document: ManagedDocument): Promise<string> {
    if (document.docHash) {
      return document.docHash;
    }
    if (document.blob) {
      return await hashBlob(document.blob);
    }
    return await hashDataUrl(document.dataUrl!);
  }

  async scheduleCacheCleanup(document: ManagedDocument): Promise<void> {
    if (document.kind !== 'pdf') {
      return;
    }
    try {
      const hash = await this.computeDocumentHash(document);
      const sixtyDaysMs = 60 * 24 * 60 * 60 * 1000;
      await this.pageCache.markDocumentForDeletion(hash, sixtyDaysMs);
    } catch (error) {
      console.warn('Failed to schedule cache cleanup', error);
    }
  }
}
